/**
 * Schemas Zod do dashboard de analytics (Fase 8). Compartilhados entre
 * API Hono (`/analytics/*`) e o admin React.
 *
 * Convenções:
 *   - period = janela temporal pedida pelo usuário, sempre presente
 *   - comparar = quando true, a API devolve a mesma estrutura para o
 *     período anterior equivalente, embutida em `previous`
 *   - todas as métricas absolutas são `number` non-negative; taxas
 *     vêm em fração 0..1 (bounceRate=0.42 ≡ 42%)
 */
import { z } from 'zod';

import { isoDateTimeSchema } from './common.js';
import { tipoLeadSchema } from './lead.js';

export const analyticsPeriodSchema = z
  .object({
    range: z.enum(['7d', '30d', '90d', 'custom']).default('30d'),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    comparar: z.coerce.boolean().default(false),
  })
  .refine((v) => v.range !== 'custom' || (v.from && v.to), {
    message: 'range=custom requer from e to no formato YYYY-MM-DD.',
  });
export type AnalyticsPeriod = z.infer<typeof analyticsPeriodSchema>;

/**
 * Dimensões de breakdown suportadas. `page` é caminho relativo (`/`);
 * `source` é referrer agrupado (Google/Direct/...); `country`/`uf` ISO/IBGE;
 * `device` Plausible classifica em mobile/desktop/tablet.
 */
export const analyticsDimensionSchema = z.enum([
  'page',
  'source',
  'country',
  'uf',
  'device',
  'city',
  'category',
]);
export type AnalyticsDimension = z.infer<typeof analyticsDimensionSchema>;

const numericMetric = z.number().nonnegative();
const rateMetric = z.number().min(0).max(1);

export const kpisSchema = z.object({
  visitors: numericMetric,
  pageviews: numericMetric,
  bounceRate: rateMetric,
  /** Média de duração de sessão em segundos. */
  avgDurationSec: numericMetric,
});
export type Kpis = z.infer<typeof kpisSchema>;

export const kpisResponseSchema = z.object({
  current: kpisSchema,
  previous: kpisSchema.nullable(),
  /** Delta percentual de cada KPI vs período anterior (`null` quando sem previous). */
  delta: z
    .object({
      visitors: z.number(),
      pageviews: z.number(),
      bounceRate: z.number(),
      avgDurationSec: z.number(),
    })
    .nullable(),
});
export type KpisResponse = z.infer<typeof kpisResponseSchema>;

export const timeseriesBucketSchema = z.object({
  /** Data ISO no formato YYYY-MM-DD (bucket diário). */
  date: z.string().date(),
  visitors: numericMetric,
  pageviews: numericMetric,
});
export type TimeseriesBucket = z.infer<typeof timeseriesBucketSchema>;

export const timeseriesResponseSchema = z.object({
  buckets: z.array(timeseriesBucketSchema),
  previousBuckets: z.array(timeseriesBucketSchema).nullable(),
});
export type TimeseriesResponse = z.infer<typeof timeseriesResponseSchema>;

export const breakdownItemSchema = z.object({
  name: z.string(),
  /** Identificador estável quando aplicável (slug de página, ISO UF). */
  key: z.string(),
  visitors: numericMetric,
  pageviews: numericMetric.optional(),
});
export type BreakdownItem = z.infer<typeof breakdownItemSchema>;

export const breakdownResponseSchema = z.object({
  dimension: analyticsDimensionSchema,
  items: z.array(breakdownItemSchema),
});
export type BreakdownResponse = z.infer<typeof breakdownResponseSchema>;

/**
 * Conversões = leads (4 tipos) + 4 eventos custom Plausible
 * (contact_form_submit, newsletter_signup, pdf_download, article_read).
 */
export const conversoesByTipoSchema = z.record(tipoLeadSchema, numericMetric);

export const conversoesEventSchema = z.object({
  name: z.string(),
  count: numericMetric,
});

export const conversoesResponseSchema = z.object({
  leads: conversoesByTipoSchema,
  customEvents: z.array(conversoesEventSchema),
  /** Buckets diários do total agregado de leads. */
  timeseries: z.array(
    z.object({
      date: z.string().date(),
      total: numericMetric,
    }),
  ),
  /** Distribuição por persona dos leads do tipo `contato`/`newsletter`. */
  porPersona: z.array(
    z.object({
      persona: z.string(),
      count: numericMetric,
    }),
  ),
});
export type ConversoesResponse = z.infer<typeof conversoesResponseSchema>;

export const analyticsHealthSchema = z.object({
  plausibleConnected: z.boolean(),
  plausibleMockMode: z.boolean(),
  eventsConfigured: z.array(z.string()),
  /** Última hora em que a API serviu uma query (smoke da própria saúde). */
  lastQueryAt: isoDateTimeSchema.nullable(),
});
export type AnalyticsHealth = z.infer<typeof analyticsHealthSchema>;
